import React, { useEffect, useState, FC, useCallback } from 'react';
import { Switch, Route, useRouteMatch, RouteProps, Redirect } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { isEmpty } from 'lodash';
import { parse } from 'query-string';
import { compose } from 'lodash/fp';

import CaptainSelectorProvider from 'src/inbox/providers/CaptainSelectorProvider';
import type { DropdownOption } from 'src/common/components/DropdownDS22';
import { fetchCatch } from 'src/core/sentry';
import { type UserRole } from 'src/types/user/User';
import { usePubSubContext } from 'src/pubsub/hook/usePubsub';
import {
  PAGES,
  COMPLETE_STATES,
  CONFIRMED_STATES,
  INQUIRY_CANCELLED_STATES,
  LAPSED_STATES,
  BOOK_NOW_STATES,
  OFFER_SENT_STATES,
  MOVED_STATES,
  ASK_TO_SEND_OFFER,
  TRIP_STATES,
  NEW_INQUIRY_STATES,
} from '../../constants';
import { isUserBanned, isUserSuspended, userIsRenter } from '../../helpers';
import {
  AccountBanned,
  AccountSuspended,
  Booking,
  BookNow,
  Cancelled,
  Complete,
  Confirmed,
  EditOffer,
  Lapsed,
  MessagesSwitcher,
  Moved,
  OfferSent,
  OwnerChangesRequested,
  Price,
  RenterChangesRequested,
  ReviewBooking,
  SendOffer,
  TripLoading,
  TripNotFound,
  UserNotAuthorized,
} from '../pages';
import { getTrip } from '../../../common/utils/reduxStoreSelectors';
import { getMessagesCall } from '../../ducks/messages';
import delay from '../../../common/utils/delay';
import { getTripCall, updateTrip } from '../../ducks/trips';
import { getBoatReviewCall, getRenterReviewCall } from '../../ducks/reviews';
import { clearTransaction, getTransactionCall } from '../../ducks/transaction';
import { type ImmutableTrip, type Trip, type TripStateState } from '../../../types/trips/Trips';
import { clearOffer } from '../../ducks/offer';
import { clearPaymentAddons, getOwnerAddonsOptionalAmountsCall, getPaymentAddonsCall } from '../../ducks/paymentAddons';
import getInboxRouteInfo from '../../../pubsub/util/getInboxRouteInfo';
import { fetchBoatAndSetMap } from '../../../common/ducks/boatMap';
import { getCancellationReasonsCall } from '../../ducks/trips';
import { getUserRole } from '../../helpers';

const { OWNER_CHANGES_REQUESTED, RENTER_CHANGES_REQUESTED } = TRIP_STATES;

const passTrip = (
  trip: ImmutableTrip,
  cancellationDropdownOptions: DropdownOption[],
) => (Page: any) => (
  (routeProps: RouteProps) => (
    <Page
      {...routeProps}
      cancellationDropdownOptions={cancellationDropdownOptions}
      trip={trip}
    />
  )
);

type MatchParams = {
  tripId: string;
};

const Trip: FC = () => {
  const match = useRouteMatch<MatchParams>();
  const url = match?.url;
  const tripId = match?.params.tripId as string;

  // Start in a loading state and flip back to loading whenever the route trip changes so we do not
  // render thread UI (e.g. Messages) against stale Redux messages from the previous trip.
  const [loading, setLoading] = useState(true);
  const [prevTripId, setPrevTripId] = useState(tripId);
  if (tripId !== prevTripId) {
    setPrevTripId(tripId);
    setLoading(true);
  }

  const [isUnauthorized, setIsUnauthorized] = useState(false);
  const [
    cancellationDropdownOptions,
    setCancellationDropdownOptions,
  ] = useState<DropdownOption[]>([]);

  const dispatch = useDispatch();
  const getTheTrip = compose(dispatch, getTripCall);
  const getPaymentAddons = compose(dispatch, getPaymentAddonsCall);
  const clearOutPaymentAddons = compose(dispatch, clearPaymentAddons);
  const getOwnerAddonsOptionalAmounts = compose(dispatch, getOwnerAddonsOptionalAmountsCall);
  const getMessages = compose(dispatch, getMessagesCall);
  const getTransaction = compose(dispatch, getTransactionCall);
  const flushTransaction = compose(dispatch, clearTransaction);
  const getRenterReview = compose(dispatch, getRenterReviewCall);
  const getBoatReview = compose(dispatch, getBoatReviewCall);
  const clearOutOffer = compose(dispatch, clearOffer);
  const updateTheTrip = compose(dispatch, updateTrip);
  const getBoatDetails = compose(dispatch, fetchBoatAndSetMap);

  const trip = useSelector(getTrip(tripId));
  const tripState = trip?.getIn(['state', 'state']) as TripStateState;
  const userRole = getUserRole(trip);
  const { seenThreadMessages } = usePubSubContext();

  const updateTripDetails = useCallback(
    async (id) => {
      setLoading(true);

      const qs = parse(window.location.search);
      // This delay is to reduce the likelihood of the frontend requesting the trip state before the
      // stripe webhook has completed being processed on the backend.
      const delayDuration = qs.session_id != null ? 1500 : 0;
      // Fetch trip first so unread counts aren't reset by the messages fetch
      const tripInfo = await delay(delayDuration, getTheTrip(id) as ImmutableTrip);
      const boatId = tripInfo.get('boat').get('id');
      const tripHasCharges = tripInfo.getIn(['state', 'has_charges']);
      const tripHasPendingCharges = tripInfo.getIn(['state', 'has_pending_charges']);
      const tripStateState = tripInfo.getIn(['state', 'state']) as TripStateState;
      const isRenter = userIsRenter(tripInfo);
      const boatCurrency = tripInfo.getIn(['listing_currency', 'code']);

      // Reset unseen state change when the user is on the trip that receives a state change
      const { view } = getInboxRouteInfo();
      const hasUnseenStateChanges = tripInfo.get('has_unseen_state_change', false);
      if (hasUnseenStateChanges && view === null) {
        const isNewInquiry = (
          tripInfo.get('last_seen_message_id') === null
          && NEW_INQUIRY_STATES.includes(tripStateState)
          && tripInfo.get('unread_message_count') === 1
        );
        updateTheTrip(tripInfo.update('has_unseen_state_change', () => false));
        if (isNewInquiry) {
          // Mark new inquiry message as read
          seenThreadMessages(`${tripInfo.get('pk') ?? ''}`);
        }
      }

      // Messages are fetched after the trip data is available
      const messagePromise = getMessages(id)
        .then((promise: any) => promise)
        .then(async (response: any) => {
          setIsUnauthorized(false);
          return response;
        })
        .catch((response: any) => {
          setIsUnauthorized(response.status === 401);
        });

      // TODO: implement a better catch here.
      // Which clears out the unreads, which means that when we get the
      // trip a second time, it shows up with everything read.
      const boatPromise = messagePromise.then(
        // Only pass along the boatCurrency if user is not the renter
        () => getBoatDetails(boatId, !isRenter && boatCurrency),
      );
      const renterReviewPromise = COMPLETE_STATES.includes(tripStateState)
        ? messagePromise.then(() => getRenterReview(id))
        : Promise.resolve();
      const boatReviewPromise = COMPLETE_STATES.includes(tripStateState)
        ? messagePromise.then(() => getBoatReview(id))
        : Promise.resolve();
      // Only fetch transaction when the trip has a reservation (backend returns 404 otherwise).
      const transactionPromise = NEW_INQUIRY_STATES.includes(tripStateState)
        ? Promise.resolve()
        : getTransaction(id).catch((response: { status: number }) => {
          // No transaction: offer has no subtotal/currency (400) or no reservation yet (404).
          if (response.status !== 400 && response.status !== 404) {
            // eslint-disable-next-line @typescript-eslint/no-throw-literal
            throw response;
          }
        });

      const paymentAddonsPromise = (tripHasCharges || tripHasPendingCharges)
        ? getPaymentAddons(id, isRenter)
        : Promise.resolve();

      const ownerAddonsOptionalAmountsPromise = (!isRenter
        && CONFIRMED_STATES.includes(tripStateState))
        ? getOwnerAddonsOptionalAmounts(id)
        : Promise.resolve();

      // Clear previously set transaction
      flushTransaction();

      // Execute all of them, short-circuit if one of them fails,
      // and squelch the error.
      Promise.all([
        messagePromise,
        boatPromise,
        renterReviewPromise,
        boatReviewPromise,
        transactionPromise,
        paymentAddonsPromise,
        ownerAddonsOptionalAmountsPromise,
      ])
        .catch(() => {})
        .finally(() => setLoading(false));
    },
    [
      flushTransaction,
      getBoatDetails,
      getBoatReview,
      getMessages,
      getRenterReview,
      getTheTrip,
      getTransaction,
      getPaymentAddons,
      getOwnerAddonsOptionalAmounts,
      seenThreadMessages,
      updateTheTrip,
    ],
  );

  const fetchCancellationOptions = useCallback(async (tripID: Trip['pk'], role: UserRole) => {
    try {
      const reasons = await dispatch<any>(getCancellationReasonsCall(tripID, role)) as string[];
      if (reasons && reasons.length) {
        const options = reasons.map((choice: string) => ({
          id: choice,
          dropdownLabel: choice,
          selectedLabel: choice,
        }));
        setCancellationDropdownOptions(options);
      }
    } catch (error) {
      fetchCatch(error);
    }
  }, [dispatch]);

  useEffect(() => {
    clearOutOffer();
    clearOutPaymentAddons();
    updateTripDetails(tripId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tripId]);

  // Load cancellation reasons when we have a userRole
  useEffect(() => {
    if (tripId && userRole) {
      fetchCancellationOptions(Number(tripId), userRole);
    }
  }, [fetchCancellationOptions, tripId, userRole]);

  if (isUnauthorized) {
    return <UserNotAuthorized />;
  }

  if (loading) {
    return <TripLoading />;
  }

  if (isEmpty(trip)) {
    return <TripNotFound />;
  }

  const passThisTrip = passTrip(trip, cancellationDropdownOptions);

  return (
    <CaptainSelectorProvider value={trip}>
      <Switch>
        <Route
          path={`${url}/${PAGES.PRICE}/`}
          render={passThisTrip(Price)}
        />
        <Route
          path={`${url}/${PAGES.EDIT}/`}
          render={passThisTrip(EditOffer)}
        />
        <Route
          path={`${url}/${PAGES.MESSAGES}/`}
          render={passThisTrip(MessagesSwitcher)}
        />
        <Route path={`${url}/${PAGES.REVIEW_BOOKING}/`}>
          {BOOK_NOW_STATES.includes(tripState)
            ? passThisTrip(ReviewBooking)
            : <Redirect to={`${url}/`} />}
        </Route>
        {COMPLETE_STATES.includes(tripState) && <Route render={passThisTrip(Complete)} />}
        {CONFIRMED_STATES.includes(tripState) && <Route render={passThisTrip(Confirmed)} />}
        {INQUIRY_CANCELLED_STATES.includes(tripState) && <Route render={passThisTrip(Cancelled)} />}
        {LAPSED_STATES.includes(tripState) && <Route render={passThisTrip(Lapsed)} />}
        {BOOK_NOW_STATES.includes(tripState) && <BookNow trip={trip} />}
        {tripState === OWNER_CHANGES_REQUESTED && (
          <Route render={passThisTrip(OwnerChangesRequested)} />
        )}
        {tripState === RENTER_CHANGES_REQUESTED && (
          <Route render={passThisTrip(RenterChangesRequested)} />
        )}
        {OFFER_SENT_STATES.includes(tripState) && <Route render={passThisTrip(OfferSent)} />}
        {MOVED_STATES.includes(tripState) && <Route render={passThisTrip(Moved)} />}
        {ASK_TO_SEND_OFFER.includes(tripState) && <Route render={passThisTrip(SendOffer)} />}
        {isUserBanned(trip) && <Route render={passThisTrip(AccountBanned)} />}
        {isUserSuspended(trip) && <Route render={passThisTrip(AccountSuspended)} />}
        {/* TODO: divide Booking component */}
        <Route render={passThisTrip(Booking)} />
      </Switch>
    </CaptainSelectorProvider>
  );
};

export default Trip;
